// What happened around a session's flagged findings, as a few moments a
// reader can take in at once (lenses/FindingStory.jsx): the person's last ask
// before the first flagged message, the calls that failed on the way there,
// one flag for each finding holding the messages it cites, and the agent's
// answer to the person after the last, if it gave one before they spoke
// again. Everything is read from the
// record: labels are the call's own subject, the message's opening words or
// the finding's title, never a model's.
//
// Positions are indexes into `turns` (a page of the record, gaps included),
// not the record's turn numbers: the page starts mid-session.
import { buildTraceLanes, markKindOf } from "./traceLanes.js";
import { blockKind } from "./traceSearch.js";

const LABEL_MAX = 30;
const TITLE_MAX = 44;
const QUOTE_MAX = 4000;
const CONTEXT_MAX = 90;
// More messages than this under one finding keep the first and the last.
const MOMENTS_MAX = 8;
// A command beside the part of its result a check quoted, in a line.
const BRIEF_COMMAND_MAX = 60;
const FAILED_MAX = 3;

const str = (v) => (v == null ? "" : String(v));
const clip = (v, n) => {
    const s = str(v).replace(/\s+/g, " ").trim();
    return s.length > n ? `${s.slice(0, n - 3)}...` : s;
};
const idOf = (v) => str(v).trim().toLowerCase();
// What the harness wrapped around a message for the agent alone, which no
// person wrote or read: a reminder, a token count, a slash command's echo.
const INJECTED = /<(system-reminder|total_tokens|command-name|command-args|command-message|local-command-stdout|local-command-stderr)>[\s\S]*?<\/\1>/g;
const scrub = (s) => str(s).replace(INJECTED, "").replace(/<system-reminder>[\s\S]*$/, "").trim();
const textOf = (c) => scrub(typeof c === "string" ? c : Array.isArray(c) ? c.map((p) => (typeof p === "string" ? p : p?.text || "")).join(" ") : "");
const NOTICE = /^\[[^\]\n]*(?:NOTIFICATION|NOT USER INPUT|AUTOMATED)[^\]\n]*\]|^<task-notification\b/i;
// A user-role turn holding words a person typed, not only what a harness sent
// in their role (a recap request, a compaction summary, another agent's
// message), by the rule the Context panel's User filter uses.
const typedByPerson = (t) => t?.role === "user" && (t.blocks || []).some((b) => b?.type === "text" && blockKind(t, b) === "user");

// What a call was asked to do: its command, path, address or query.
function subjectOf(block) {
    const inp = block?.input && typeof block.input === "object" ? block.input : {};
    const what = inp.command ?? inp.cmd ?? inp.file_path ?? inp.path ?? inp.url ?? inp.query ?? inp.pattern ?? inp.description;
    return what != null ? str(what) : block?.input != null ? JSON.stringify(block.input) : "";
}

// A turn as the record has it: who it is (person, notice, ran, result,
// failed, said, thought), and its call or text in a label, a line and in
// full. A call is quoted with what came back from it (`results`: call id ->
// its result), so a part a check quoted from the result reads beside the
// command that produced it.
function readTurn(turns, i, calls, results = null) {
    const t = turns[i];
    const blocks = Array.isArray(t?.blocks) ? t.blocks : [];
    const use = blocks.find((b) => b?.type === "tool_use");
    if (use) {
        const subject = subjectOf(use);
        const res = use.id ? results?.get(use.id) : null;
        const out = res ? textOf(res.content) : null;
        return {
            who: "ran", label: subject || use.name || "a call", line: `${use.name || "Call"} ${subject}`.trim(),
            quote: `${use.name || "Call"}: ${subject}${res ? `\n${res.isError ? "Failed: " : "-> "}${out || "(no output)"}` : ""}`.trim(),
            command: subject || null, output: out,
        };
    }
    const res = blocks.find((b) => b?.type === "tool_result");
    if (res) {
        const call = calls.get(res.tool_use_id);
        const subject = call ? subjectOf(call.block) : "";
        const out = textOf(res.content);
        return {
            who: res.isError ? "failed" : "result",
            label: `${res.isError ? "Failed: " : ""}${subject || call?.block?.name || "a result"}`,
            line: `${res.isError ? "failed: " : ""}${out || "(no output)"}`,
            quote: `${call ? `${call.block.name || "Call"}: ${subject}\n` : ""}${res.isError ? "Failed: " : "Returned: "}${out || "(no output)"}`,
            command: subject || null, output: out,
        };
    }
    const said = textOf(blocks.filter((b) => b?.type === "text").map((b) => b.text || ""));
    const thought = textOf(blocks.filter((b) => b?.type === "thinking").map((b) => b.thinking || b.text || ""));
    const text = said || thought;
    const person = t?.role === "user";
    const who = person ? (!said || NOTICE.test(said) || !typedByPerson(t) ? "notice" : "person") : said ? "said" : "thought";
    return { who, label: text ? `"${text}"` : "(no text)", line: text || "(no text)", quote: text || "(no text)" };
}

// The flagged findings' messages placed in these turns, each turn once:
// [{ turn, met, findings }], met when any finding citing it met a standard,
// findings the indexes of those citing it.
export function citedTurns(turns, findings) {
    const at = new Map();
    (turns || []).forEach((t, i) => { const id = idOf(t?.messageId); if (id && !at.has(id)) at.set(id, i); });
    const out = new Map();
    (findings || []).forEach((f, k) => {
        for (const id of f?.messageIds || []) {
            const i = at.get(idOf(id));
            if (i == null) continue;
            const cur = out.get(i) || { turn: i, met: false, findings: [] };
            cur.met = cur.met || !!f.met;
            if (!cur.findings.includes(k)) cur.findings.push(k);
            out.set(i, cur);
        }
    });
    return [...out.values()].sort((a, b) => a.turn - b.turn);
}

// A reader of one page of turns: a moment at any turn, { turn, at, who,
// label, quote, highlight, before, after, messageId }, or null for a gap or
// no turn. `quotes`: message id -> what a check quoted from it: the part of
// the turn's own text to highlight, or, where the text does not hold it, the
// quote itself. The lines either side skip what no person saw.
function momentReader(list, quotes = null) {
    const calls = new Map();
    const results = new Map();
    list.forEach((t, j) => {
        for (const b of t?.blocks || []) {
            if (b?.type === "tool_use" && b.id) calls.set(b.id, { block: b, i: j });
            if (b?.type === "tool_result" && b.tool_use_id && !results.has(b.tool_use_id)) results.set(b.tool_use_id, b);
        }
    });
    const live = (j) => list[j] && list[j].role !== "gap";
    const read = (j) => readTurn(list, j, calls, results);
    const near = (i, step) => {
        for (let j = i + step; j >= 0 && j < list.length; j += step) {
            if (live(j) && read(j).who !== "notice") return j;
        }
        return null;
    };
    const line = (j) => (j == null ? null : clip(read(j).line, CONTEXT_MAX));
    return (i) => {
        if (!live(i)) return null;
        const r = read(i);
        const quoted = clip(quotes?.get(idOf(list[i].messageId)) || "", QUOTE_MAX);
        const text = clip(r.quote, QUOTE_MAX);
        const held = quoted && text.includes(quoted);
        return {
            turn: i, at: Number.isFinite(list[i].messageAt) ? list[i].messageAt : null, who: r.who, label: clip(r.label, LABEL_MAX),
            quote: quoted && !held ? quoted : text, highlight: held ? quoted : null, quoted: !!quoted,
            brief: briefOf(r, held ? quoted : null),
            before: line(near(i, -1)), after: line(near(i, 1)), messageId: list[i].messageId || null,
        };
    };
}

// A message in a line, where a check quoted part of it: that part, with the
// command it came back from when it came back from one. Null otherwise.
function briefOf(r, quoted) {
    if (!quoted) return null;
    const whole = clip(r.command || "", QUOTE_MAX);
    if (!whole || whole.includes(quoted)) return quoted;
    return `${clip(lastStatement(r.command), BRIEF_COMMAND_MAX)} -> ${quoted}`;
}

// A script's last statement that does something: what produced its output,
// where the script first sets things up (a PR body, a variable) and only
// then runs the command.
function lastStatement(command) {
    const parts = str(command).split(/\r?\n|;|&&/).map((p) => p.trim()).filter((p) => p.length >= 6 && /[A-Za-z]{2,}/.test(p) && !/^['"]?@['"]?$/.test(p));
    return parts.length ? parts[parts.length - 1] : str(command);
}

// One turn of these, as a moment's panel reads it.
export function turnMoment(turns, i) {
    return momentReader(Array.isArray(turns) ? turns : [])(i);
}

const keepEnds = (list, n) => (list.length <= n ? list : [...list.slice(0, Math.ceil(n / 2)), ...list.slice(-Math.floor(n / 2))]);

// -> null when no flagged message is in these turns, else
// { from, to, flags, steps, met }:
//   from/to: the stretch of turns drawn
//   flags: [{ key, kind: ask|failed|finding|told, turn, label, met,
//             finding?, title?, standard?, why?, moments }], in order of their
//          first moment; a finding
//          flag's moments are the messages it cites, the others' their one
//   steps: [{ flag, moment }], every moment in the order the arrow keys
//          step through them; a message several findings cite is one step,
//          under the first
// `findings`: [{ title, messageIds, met, quotes: [{ messageId, text }],
// standard, why }], the standard that met it and its reason.
export function findingStory(turns, findings) {
    const list = Array.isArray(turns) ? turns : [];
    const cited = citedTurns(list, findings);
    if (!cited.length) return null;
    const live = (i) => list[i] && list[i].role !== "gap";
    const firstCited = cited[0].turn;
    const lastCited = cited[cited.length - 1].turn;

    let model = null;
    try { model = buildTraceLanes(list); } catch { model = null; }
    const lanes = model?.lanes || [];
    const items = (domain) => lanes.filter((l) => l.domain === domain).flatMap((l) => l.items || [])
        .filter((it) => Number.isInteger(it.turnIndex) && live(it.turnIndex)).sort((a, b) => a.turnIndex - b.turnIndex);

    const quotes = new Map();
    for (const f of findings || []) for (const q of f?.quotes || []) if (q?.messageId && q.text && !quotes.has(idOf(q.messageId))) quotes.set(idOf(q.messageId), str(q.text));
    const momentAt = momentReader(list, quotes);
    const callTurnOf = (i) => {
        const res = (list[i]?.blocks || []).find((b) => b?.type === "tool_result");
        if (!res) return i;
        const at = list.findIndex((t) => (t?.blocks || []).some((b) => b?.type === "tool_use" && b.id === res.tool_use_id));
        return at >= 0 ? at : i;
    };
    const resultOf = (i) => {
        const id = (list[i]?.blocks || []).find((b) => b?.type === "tool_use")?.id;
        const at = id ? list.findIndex((t) => (t?.blocks || []).some((b) => b?.type === "tool_result" && b.tool_use_id === id)) : -1;
        return at >= 0 ? at : i;
    };

    const asked = (it) => it.op?.kind !== "notice" && typedByPerson(list[it.turnIndex]);
    const asks = items("prompt").filter((it) => asked(it) && it.turnIndex <= firstCited);
    const ask = asks[asks.length - 1] || null;
    // The agent's answer to the person, if it gave one before they spoke
    // again: a reply after that answers something else.
    const nextAsk = items("prompt").find((it) => asked(it) && it.turnIndex > lastCited);
    const told = items("reply").find((it) => it.turnIndex > lastCited && (!nextAsk || it.turnIndex < nextAsk.turnIndex)) || null;
    const citedAt = new Set(cited.map((c) => c.turn));
    // With no ask before it on these turns, failures from the page's start.
    const startAt = ask ? ask.turnIndex : -1;
    const failedTurns = [...new Set(lanes.flatMap((l) => l.items || [])
        .filter((it) => markKindOf(it) === "error" && Number.isInteger(it.turnIndex) && it.turnIndex > startAt && it.turnIndex < lastCited && live(it.turnIndex))
        .map((it) => it.turnIndex))]
        .filter((t) => !citedAt.has(t) && ![...citedAt].some((c) => callTurnOf(c) === t))
        .sort((a, b) => a - b);
    // The failures nearest the first flagged message say most about it.
    const failed = failedTurns.filter((t) => t < firstCited).slice(-FAILED_MAX);

    const one = (kind, turn, moment, extra = {}) => ({ key: `${kind}:${turn}`, kind, turn, label: moment.label, met: false, moments: [moment], ...extra });
    const flags = [];
    // An ask a finding cites is one of its moments already: it stands there
    // once, not again as the ask.
    if (ask && !citedAt.has(ask.turnIndex)) {
        const m = momentAt(ask.turnIndex);
        flags.push(one("ask", ask.turnIndex, { ...m, label: clip(`"${ask.op?.text || m.quote}"`, LABEL_MAX), quote: clip(ask.op?.text || m.quote, QUOTE_MAX), who: "person" }));
    }
    // A failure's mark stands at its call; its result says what failed.
    for (const t of failed) {
        const m = momentAt(resultOf(t));
        flags.push(one("failed", t, { ...m, turn: t }, { label: momentAt(t)?.label || m.label }));
    }
    (findings || []).forEach((f, k) => {
        const mine = cited.filter((c) => c.findings.includes(k));
        if (!mine.length) return;
        const moments = keepEnds(mine, MOMENTS_MAX).map((c) => momentAt(c.turn)).filter(Boolean);
        if (!moments.length) return;
        flags.push({
            key: `finding:${k}`, kind: "finding", finding: k, turn: moments[0].turn, met: !!f.met, moments,
            label: clip(f.title || "Flagged finding", TITLE_MAX), title: str(f.title || "Flagged finding"),
            standard: f.standard || null, why: f.why || null,
        });
    });
    if (told) {
        const m = momentAt(told.turnIndex);
        flags.push(one("told", told.turnIndex, { ...m, label: clip(`"${told.op?.text || m.quote}"`, LABEL_MAX), quote: clip(told.op?.text || m.quote, QUOTE_MAX), who: "said" }));
    }
    flags.sort((a, b) => a.turn - b.turn || (a.kind === "finding") - (b.kind === "finding"));
    const stepped = new Set();
    const steps = flags.flatMap((f, i) => f.moments.map((m, j) => ({ flag: i, moment: j, turn: m.turn })))
        .filter((s) => !stepped.has(s.turn) && stepped.add(s.turn))
        .map(({ flag, moment }) => ({ flag, moment }));

    const from = ask ? ask.turnIndex : (failed[0] ?? firstCited);
    const to = told ? told.turnIndex : lastCited;
    return { from, to, flags, steps, met: cited.some((c) => c.met) };
}
